/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import moment from 'moment'
import { useState } from 'react'
import Button from 'react-bootstrap/Button'

import { confirm } from 'components/molecules/Confirmation'
import { faBan, faRedo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCancelFileFaxMutation, useSendFaxMutation } from 'store/services/labOrder'
import { Order } from 'store/types'
import { getFaxFailureDetailsByMessage } from 'components/lab/fax/faxFailureDisplay'
import { SendReferralPacketModal } from './SendReferralPacketModal'

const ListGroupItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.85rem 1rem;
`

const FilenameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.25rem;
  font-size: 0.85rem;
  opacity: 0.75;
`

const statusVariant = (status?: string) => {
  if (status === 'FAILED') return 'danger'
  if (status === 'SENT') return 'success'
  if (status === 'PENDING') return 'warning'
  if (status === 'CANCELLED') return 'secondary'
  return 'secondary'
}

type IProps = {
  order: Order
}

export const FaxesPanel = ({ order }: IProps) => {
  const [showReferralPacket, setShowReferralPacket] = useState(false)
  const [sendFax, { isLoading: isRetrying }] = useSendFaxMutation()
  const [cancelFileFax, { isLoading: isCancelling }] = useCancelFileFaxMutation()
  const faxItems = Object.values(order?.files || {})
    .filter((file) => file.faxStatus)
    .sort((a, b) => {
      const first = a.lastFaxAttempt ? new Date(a.lastFaxAttempt).getTime() : 0
      const second = b.lastFaxAttempt ? new Date(b.lastFaxAttempt).getTime() : 0

      return second - first
    })

  // A signed PAP Rx that came in by hand, before the order was complete, or with
  // no DME on file is sent from here (TES-3159).
  const referralPacket = order?.testType === 'HST' && (
    <>
      <div className="d-flex justify-content-end mb-2">
        <Button variant="outline-primary" size="sm" onClick={() => setShowReferralPacket(true)}>
          Send referral packet
        </Button>
      </div>
      {showReferralPacket && (
        <SendReferralPacketModal order={order} onClose={() => setShowReferralPacket(false)} />
      )}
    </>
  )

  if (!faxItems.length) {
    return (
      <>
        {referralPacket}
        <div className="text-muted">No faxes have been sent from this order yet.</div>
      </>
    )
  }

  return (
    <>
    {referralPacket}
    <div className="list-group text-secondary">
      {faxItems.map((file) => {
        const nextStep = getFaxFailureDetailsByMessage(file.faxError)?.nextStep

        return (
        <ListGroupItem
          key={`${file.key}-${file.faxStatus}-${file.lastFaxAttempt || 'none'}`}
          className="list-group-item">
          <div className="d-flex align-items-start justify-content-between w-100">
            <FilenameWrapper>
              <div className="d-flex align-items-center flex-wrap">
                <span className={`badge badge-${statusVariant(file.faxStatus)} mr-2`}>
                  {file.faxStatus}
                </span>
                {file.previewUrl || file.url ? (
                  // A plain link: the router's Link would treat the S3 URL as a
                  // path under the current page.
                  <a href={file.previewUrl || file.url} target="_blank" rel="noreferrer">
                    {file.filename}
                  </a>
                ) : (
                  <span>{file.filename}</span>
                )}
              </div>
              <MetaRow>
                {file.recipientName && <span>To: {file.recipientName}</span>}
                <span>Type: {file.uploadType || '-'}</span>
                <span>Fax Number: {file.faxedTo || '-'}</span>
                <span>
                  Last Attempt:{' '}
                  {file.lastFaxAttempt ? moment(file.lastFaxAttempt).format('lll') : '-'}
                </span>
              </MetaRow>
              {!!file.contents?.length && (
                <div className="mt-1" css={{ fontSize: '0.85rem' }} data-testid="fax-contents">
                  Included: {file.contents.join(', ')}
                </div>
              )}
              {file.faxError && (
                <>
                  <div className="text-danger mt-2" css={{ fontSize: '0.9rem' }}>
                    {file.faxError}
                  </div>
                  {nextStep && (
                    <div className="mt-1" css={{ fontSize: '0.9rem' }}>
                      Next step: {nextStep}
                    </div>
                  )}
                </>
              )}
            </FilenameWrapper>
            {(file.faxStatus === 'FAILED' || file.faxStatus === 'PENDING') && (
              <div className="ml-3 d-flex" css={{gap: '0.25rem'}}>
                {file.faxStatus === 'FAILED' && (
                  <Button
                    style={{ borderRadius: '50%' }}
                    variant="outline-primary"
                    size="sm"
                    disabled={!file.faxedTo || isRetrying}
                    title={file.faxError || undefined}
                    onClick={() =>
                      confirm('', {
                        title: 'Retry faxing?',
                        description: 'This will queue this file to be faxed again.',
                        yesAction: 'Retry',
                        noAction: 'Cancel',
                      }).then(() =>
                        sendFax({
                          orderId: Number(order.id),
                          documentKeys: [file.key],
                          faxNumber: file.faxedTo || '',
                          message: '',
                        }).unwrap(),
                      )
                    }>
                    <FontAwesomeIcon icon={faRedo} />
                  </Button>
                )}
                <Button
                  style={{ borderRadius: '50%' }}
                  variant="outline-danger"
                  size="sm"
                  disabled={isCancelling}
                  title="Cancel fax"
                  onClick={() =>
                    confirm('', {
                      title: 'Cancel fax?',
                      description: 'This will cancel the pending fax for this file.',
                      yesAction: 'Cancel fax',
                      noAction: 'Keep',
                    }).then(() =>
                      cancelFileFax({
                        orderId: Number(order.id),
                        documentKeys: [file.key],
                      }).unwrap(),
                    )
                  }>
                  <FontAwesomeIcon icon={faBan} />
                </Button>
              </div>
            )}
          </div>
        </ListGroupItem>
        )
      })}
    </div>
    </>
  )
}
